export interface JourneyIslandLike {
  id: string;
}

export interface JourneyStationLike {
  id: string;
  island_id: string;
}

export interface JourneyIslandProgressLike {
  user_id?: string | null;
  island_id: string;
  score?: number | null;
  completed_at?: string | null;
}

export interface JourneyStationProgressLike {
  user_id?: string | null;
  station_id: string;
  station_points?: number | null;
}

function safePoints(value: number | null | undefined) {
  const points = Number(value ?? 0);
  return Number.isFinite(points) ? Math.max(0, points) : 0;
}

export function filterProgressForUser<
  TProgress extends { user_id?: string | null },
>(progress: TProgress[] | null | undefined, userId: string | null | undefined) {
  if (!userId) return [];

  return (progress ?? []).filter((item) => item.user_id === userId);
}

export function filterIslandProgressForJourney<
  TProgress extends JourneyIslandProgressLike,
>(
  progress: TProgress[] | null | undefined,
  userId: string | null | undefined,
  islandIds: Set<string>,
) {
  return filterProgressForUser(progress, userId).filter((item) =>
    islandIds.has(item.island_id),
  );
}

export function filterStationProgressForJourney<
  TProgress extends JourneyStationProgressLike,
>(
  progress: TProgress[] | null | undefined,
  userId: string | null | undefined,
  stationIds: Set<string>,
) {
  return filterProgressForUser(progress, userId).filter((item) =>
    stationIds.has(item.station_id),
  );
}

export function completedIslandIdsFromProgress(
  progress: JourneyIslandProgressLike[],
) {
  return new Set(
    progress
      .filter((item) => Boolean(item.completed_at))
      .map((item) => item.island_id),
  );
}

export function currentIslandIdFromProgress(
  islands: JourneyIslandLike[],
  completedIslandIds: Set<string>,
) {
  return (
    islands.find((island) => !completedIslandIds.has(island.id))?.id ?? null
  );
}

export function calculateJourneyScore(
  islands: JourneyIslandLike[],
  stations: JourneyStationLike[],
  stationProgress: JourneyStationProgressLike[],
  islandProgress: JourneyIslandProgressLike[],
  bonusPoints = 0,
) {
  const stationIslandIds = new Map(
    stations.map((station) => [station.id, station.island_id]),
  );
  const stationScoresByIsland = new Map<string, number>();

  stationProgress.forEach((progress) => {
    const islandId = stationIslandIds.get(progress.station_id);
    if (!islandId) return;

    stationScoresByIsland.set(
      islandId,
      (stationScoresByIsland.get(islandId) ?? 0) +
        safePoints(progress.station_points),
    );
  });

  const completedScoresByIsland = new Map(
    islandProgress.map((progress) => [
      progress.island_id,
      safePoints(progress.score),
    ]),
  );

  const journeyScore = islands.reduce((sum, island) => {
    const stationScore = stationScoresByIsland.get(island.id) ?? 0;
    const completedScore = completedScoresByIsland.get(island.id) ?? 0;
    return sum + Math.max(stationScore, completedScore);
  }, 0);

  return journeyScore + safePoints(bonusPoints);
}
